import type { QuerySpec } from '@helios/powersync-client-core';

// ─── Types ───

/** Which configured clinic logo to render on the PO printout. */
export type PurchaseOrderPrintLogoSource = 'standard' | 'horizontal' | 'none';

/** PowerSync sqlite row shape — booleans are stored as 0/1 integers. */
export interface PurchaseOrderPrintSettingsRow {
  id: string;
  org_id: string | null;
  clinic_id: string | null;
  logo_source: string | null;
  buyer_name: string | null;
  buyer_address: string | null;
  buyer_phone: string | null;
  buyer_email: string | null;
  buyer_tax_id: string | null;
  footer_text: string | null;
  terms_text: string | null;
  prepared_by_label: string | null;
  approved_by_label: string | null;
  supplier_acknowledgment_label: string | null;
  show_sku: number | null;
  show_bonus: number | null;
  show_remarks: number | null;
  show_supplier_contact: number | null;
  show_signature_block: number | null;
  show_terms: number | null;
  created_at: string | null;
  updated_at: string | null;
  updated_by: string | null;
}

/** UI-friendly settings shape used by the wizard + the print layout. */
export interface PurchaseOrderPrintSettingsUI {
  id: string;
  logoSource: PurchaseOrderPrintLogoSource;

  buyerName: string;
  buyerAddress: string;
  buyerPhone: string;
  buyerEmail: string;
  buyerTaxId: string;

  footerText: string;
  termsText: string;

  preparedByLabel: string;
  approvedByLabel: string;
  supplierAcknowledgmentLabel: string;

  showSku: boolean;
  showBonus: boolean;
  showRemarks: boolean;
  showSupplierContact: boolean;
  showSignatureBlock: boolean;
  showTerms: boolean;
}

/**
 * PRD.239 — Fallback defaults. The PO print mount uses these only when
 * the clinic has not yet saved a `purchase_order_print_settings` row.
 * Once a row exists, the saved values take precedence and these become
 * irrelevant for that org.
 */
export const PURCHASE_ORDER_PRINT_DEFAULTS: Omit<PurchaseOrderPrintSettingsUI, 'id'> = {
  logoSource: 'standard',

  buyerName: '',
  buyerAddress: '',
  buyerPhone: '',
  buyerEmail: '',
  buyerTaxId: '',

  footerText: '',
  termsText:
    'Goods are to be supplied per the agreed specifications and delivery schedule. Please reference the PO number on all related correspondence and shipping documents.',

  preparedByLabel: 'Prepared by',
  approvedByLabel: 'Approved by',
  supplierAcknowledgmentLabel: 'Supplier acknowledgment',

  showSku: true,
  showBonus: true,
  showRemarks: true,
  showSupplierContact: true,
  showSignatureBlock: true,
  showTerms: true,
};

// ─── Queries ───

/** Fetch the org-default Purchase Order Print Settings row (clinic_id IS NULL). */
export function qPurchaseOrderPrintSettings(): QuerySpec<[]> {
  return {
    key: ['purchase-order-print-settings'] as const,
    sql: `SELECT * FROM purchase_order_print_settings WHERE clinic_id IS NULL ORDER BY updated_at DESC LIMIT 1`,
    params: [],
    watch: ['purchase_order_print_settings'],
  };
}

// ─── Mappers ───

function asBool(v: number | null, fallback: boolean): boolean {
  if (v == null) return fallback;
  return v === 1;
}

export function mapPurchaseOrderPrintSettingsToUI(
  row: PurchaseOrderPrintSettingsRow,
): PurchaseOrderPrintSettingsUI {
  const d = PURCHASE_ORDER_PRINT_DEFAULTS;
  return {
    id: row.id,
    logoSource: (row.logo_source ?? d.logoSource) as PurchaseOrderPrintLogoSource,

    buyerName: row.buyer_name ?? '',
    buyerAddress: row.buyer_address ?? '',
    buyerPhone: row.buyer_phone ?? '',
    buyerEmail: row.buyer_email ?? '',
    buyerTaxId: row.buyer_tax_id ?? '',

    footerText: row.footer_text ?? '',
    termsText: row.terms_text ?? d.termsText,

    preparedByLabel: row.prepared_by_label ?? d.preparedByLabel,
    approvedByLabel: row.approved_by_label ?? d.approvedByLabel,
    supplierAcknowledgmentLabel: row.supplier_acknowledgment_label ?? d.supplierAcknowledgmentLabel,

    showSku: asBool(row.show_sku, d.showSku),
    showBonus: asBool(row.show_bonus, d.showBonus),
    showRemarks: asBool(row.show_remarks, d.showRemarks),
    showSupplierContact: asBool(row.show_supplier_contact, d.showSupplierContact),
    showSignatureBlock: asBool(row.show_signature_block, d.showSignatureBlock),
    showTerms: asBool(row.show_terms, d.showTerms),
  };
}

export function mapUIToPurchaseOrderPrintSettingsColumns(
  ui: Omit<PurchaseOrderPrintSettingsUI, 'id'>,
) {
  const b = (v: boolean) => (v ? 1 : 0);
  return {
    logo_source: ui.logoSource,
    buyer_name: ui.buyerName || null,
    buyer_address: ui.buyerAddress || null,
    buyer_phone: ui.buyerPhone || null,
    buyer_email: ui.buyerEmail || null,
    buyer_tax_id: ui.buyerTaxId || null,
    footer_text: ui.footerText || null,
    terms_text: ui.termsText || null,
    prepared_by_label: ui.preparedByLabel,
    approved_by_label: ui.approvedByLabel,
    supplier_acknowledgment_label: ui.supplierAcknowledgmentLabel,

    show_sku: b(ui.showSku),
    show_bonus: b(ui.showBonus),
    show_remarks: b(ui.showRemarks),
    show_supplier_contact: b(ui.showSupplierContact),
    show_signature_block: b(ui.showSignatureBlock),
    show_terms: b(ui.showTerms),

    updated_at: new Date().toISOString(),
  };
}
